import {
  Button as MuiButton,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid as MuiGrid,
  IconButton,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import { AxiosError } from 'axios'
import { plainToClass } from 'class-transformer'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import React, { createRef, ReactNode, useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { ASN, ASNExpectedUnit, Sku } from 'src/api/models'
import { AttachmentProp } from 'src/components/Dialog/AddAttachmentsDialog'
import { FilePreview, UrlPreview } from 'src/components/FilePreview'
import { useQueryPaginator } from 'src/components/Table'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import {
  BooleanField,
  DateField,
  NumberField,
  QuerySelectField,
  TextareaField,
  TextField,
  useForm,
} from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import styled from 'styled-components'
import * as yup from 'yup'

const Centered = styled.div`
  text-align: center;
`

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface CustomFieldProps {
  name: string
  value: string
}

interface SkuProps {
  sku: string
  num_units: number // eslint-disable-line camelcase
}

interface ASNProps {
  facility: string
  carrier: string
  tracking_number: string // eslint-disable-line camelcase
  custom_fields: CustomFieldProps[] // eslint-disable-line camelcase
  expected_inventory: SkuProps[] // eslint-disable-line camelcase
  attachments?: AttachmentProp[]
}

const asnSchema = yup.object().shape({
  facility: yup.string().required().label('Facility'),
  is_transload: yup.boolean().default(false),
  carrier: yup.string().label('Carrier').optional(),
  tracking_number: yup.string().label('Tracking Number'),
  estimated_arrival_date: yup.string().nullable().optional().label('Estimated Arrival Date'),
  details: yup.string().optional().label('Details'),
  expected_pallet_count: yup.number().nullable().optional().integer().min(0, 'Must be positive'),
  expected_inventory: yup
    .array()
    .of(
      yup
        .object({
          sku: yup.string().required().label('SKU'),
          num_units: yup
            .number()
            .required()
            .label('Number of Units')
            .transform((currentValue, originalValue) => {
              if (currentValue === '' || originalValue === '') {
                return undefined
              }
              return currentValue
            }),
        })
        .label('Inventory')
    )
    .when('expected_pallet_count', {
      is: undefined,
      then: yup.array().min(1, 'Must include at least 1 SKU or an expected Pallet count'),
    }),
  attachments: yup.array().of(
    yup
      .object({
        xid: yup.string().when('file', {
          is: (file) => !file,
          then: yup.string().required(),
        }),
        file: yup.mixed(),
        tags: yup.array().of(yup.string()),
      })
      .label('Attachments')
  ),
})

interface ImageListWrapperProps {
  children: ReactNode
}

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  asn?: ASN
  inventory?: ASNExpectedUnit[]
}

const ImageListWrapper = (props: ImageListWrapperProps) => {
  const { children } = props
  return (
    <Grid container spacing={6} mt={6}>
      {children}
    </Grid>
  )
}

export const ASNDialog = (props: Props) => {
  const classes = useStyles()
  const { open, handleClose, onSave, update = false, asn } = props
  const [saving, setSaving] = useState(false)
  const [inventoryOpen, setInventoryOpen] = useState(true)

  const facilityPaginator = useQueryPaginator('facility-list', skuit.client.facilities.list)
  const carrierPaginator = useQueryPaginator('carrier-list', skuit.client.carriers.list)
  const skuPaginator = useQueryPaginator('sku-list', skuit.client.skus.list, {
    params: { active: 'true' },
  })
  const customFieldPaginator = useQueryPaginator(
    'custom-fields',
    skuit.client.asn.customFields.list
  )
  const customFieldValuePaginator = useQueryPaginator(
    'custom-field-values',
    skuit.client.asn.customFieldValues.list,
    {
      params: { asn: asn?.guid || 'an_noasn' },
    }
  )
  const customFieldValues: Record<string, string> = {}
  for (const field of customFieldValuePaginator.data.results) {
    customFieldValues[field.name] = field.value
  }

  const expctedUnitsPaginator = useQueryPaginator(
    'asn-expected-units',
    skuit.client.asn.expectedUnits.list,
    {
      params: { asn: asn?.guid || 'an_noasn' },
    }
  )

  const tagPaginator = useQueryPaginator('tags', skuit.client.attachments.tags.list)

  const { Form, Field, FieldList, data: formData } = useForm(asnSchema)

  const onSubmit = (data: ASNProps) => {
    setSaving(true)
    const attachments = data.attachments
    delete data.attachments

    const customFields = []
    for (const field of customFieldPaginator.data.results) {
      if (field.name in data) {
        customFields.push({
          name: field.name,
          value: (data as any)[field.name],
        })
        delete (data as any)[field.name]
      }
    }
    if (customFields.length) {
      data['custom_fields'] = customFields
    }

    let func = skuit.client.asn.create
    let action = 'creat'
    if (update) {
      func = (data: ASNProps) => skuit.client.asn.patch(asn?.guid || '', data)
      action = 'updat'
    }

    func(data)
      .then((newASN: ASN) => {
        if (attachments?.length) {
          skuit.client.asn.attachments
            .create(newASN.guid as string, { attachments })
            .then(() => {
              handleClose()
              if (onSave) {
                onSave()
              }
              toast.success(() => (
                <Notification
                  message={`Successfully ${action}ed ASN: ${newASN.name}`}
                  newObject={newASN}
                />
              ))
              toast.success(() => (
                <Notification message={`Successfully added ${attachments.length} attachments!`} />
              ))
            })
            .catch((attachmentError: AxiosError) => {
              handleErrorMessage(attachmentError, action, 'Attachments')

              setApiErrors(formData.setError, attachmentError)
            })
        } else {
          handleClose()
          if (onSave) {
            onSave()
          }
          toast.success(() => (
            <Notification message={`Successfully ${action}ed ASN: ${newASN.name}`} />
          ))
        }
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'ASN')

        setApiErrors(formData.setError, error)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const startingInventory = expctedUnitsPaginator.data?.results
    ? expctedUnitsPaginator.data.results.map((item: ASNExpectedUnit) => ({
        sku: item.sku,
        num_units: item.numUnits,
      }))
    : undefined

  const expectedInventory = formData.getValue('expected_inventory')

  // TODO: we don't need SKU availability...just need SKU lookup.
  const { mutate: submitSkus } = useMutation(skuit.client.freight.orders.skuAvailability, {
    onSuccess: (response) => {
      const newInventory = response.data.map(
        (field: { sku: Sku; num_units: number; lookup: string }, index: number) => {
          // eslint-disable-line camelcase
          const { sku, num_units, lookup } = field // eslint-disable-line camelcase
          const skuObj = plainToClass(Sku, sku)
          const existing = formData.getValue('expected_inventory')
          if (!skuObj) {
            formData.setError(
              `expected_inventory[${index + existing.length}].sku`,
              `Invalid SKU lookup: ${lookup}`
            )
          }
          return { sku: skuObj, num_units }
        }
      )
      formData.extend('expected_inventory', newInventory)
    },
    onError: (error) => {
      toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
    },
  })

  const fileInput = createRef<HTMLInputElement>()
  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const form = new FormData()
      form.append('file', e.target.files[0])
      submitSkus(form)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{asn ? 'Update' : 'Create'} Advanced Shipping Notice</DialogTitle>
        <DialogContent>
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator}
            defaultValue={asn?.facility}
          />
          <BooleanField
            name="is_transload"
            label="Transload"
            defaultValue={asn ? Boolean(asn.transloadNumber) : false}
          />
          <QuerySelectField
            name="carrier"
            label="Carrier"
            paginator={carrierPaginator}
            defaultValue={asn?.carrier}
          />
          <TextField
            name="tracking_number"
            label="Tracking Number"
            defaultValue={asn?.trackingNumber}
          />
          <NumberField
            name="expected_pallet_count"
            label="Expected Pallet Count"
            defaultValue={asn?.expectedPalletCount}
          />
          <TextareaField
            name="details"
            label="Details"
            defaultValue={asn?.details || ''}
          />
          <DateField
            name="estimated_arrival_date"
            label="Estimated Arrival Date"
            defaultValue={asn?.estimatedArrivalDate}
          />
          {customFieldPaginator.data.results.map((field) => (
            <TextField
              key={field.name}
              name={field.name}
              label={field.label}
              defaultValue={customFieldValues[field.name]}
            />
          ))}
          <Typography variant="h6" mt={6} mb={3}>
            Attachments
          </Typography>
          <FieldList
            name="attachments"
            startCount={0}
            renderHeader={({ name, value, setValue }) => (
              <DropzoneAreaBase
                dropzoneText="Drag and drop or click to add files"
                fileObjects={[]}
                onAdd={(files) => {
                  const fileData = files.map((fileObj) => {
                    return { file: fileObj.file, tags: [] }
                  })
                  const newValue = (value || []).concat(fileData)
                  setValue(name, newValue)
                }}
                filesLimit={10}
                showPreviewsInDropzone={false}
              />
            )}
            renderList={ImageListWrapper}
            renderField={({ name: itemName }) => (
              <Grid item xs={3} key={itemName}>
                <Card>
                  <CardContent>
                    <Field
                      name="file"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <FilePreview file={value} onDelete={() => remove(itemName)} />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="url"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <UrlPreview
                              url={value}
                              allowDownload={false}
                              onDelete={() => remove(itemName)}
                            />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="tags"
                      getSubmitValue={(value) =>
                        value && value.map((tag: { slug: string }) => tag.slug)
                      }
                      renderField={({ name, value, setValue, error }) => (
                        <Autocomplete
                          id={name}
                          multiple
                          freeSolo
                          options={tagPaginator.data?.results}
                          getOptionLabel={(tag) => tag.name}
                          filterSelectedOptions
                          onChange={(event: any, newValue: any | null) => setValue(name, newValue)}
                          value={value === '' ? [] : value}
                          renderInput={(params) => (
                            <MuiTextField
                              {...params}
                              error={!!error}
                              helperText={error}
                              label="Tags"
                            />
                          )}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          />
          <Typography variant="h6" mt={6} mb={3}>
            Inventory{' '}
            <IconButton onClick={() => setInventoryOpen(!inventoryOpen)}>
              {inventoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Typography>
          <Collapse in={inventoryOpen}>
            {expctedUnitsPaginator.loading ? (
              <CircularProgress color="inherit" />
            ) : (
              <FieldList
                name="expected_inventory"
                defaultValues={startingInventory}
                startCount={0}
                renderHeader={({ error }) => (
                  <>{error && <Typography color="error">{error}</Typography>}</>
                )}
                renderField={({ name: listItemName, data }) => (
                  <Grid container spacing={2} key={listItemName}>
                    <Grid item xs={5}>
                      <QuerySelectField
                        name="sku"
                        label="|SKU| Product"
                        paginator={skuPaginator}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <NumberField
                        name="num_units"
                        label="Number of Units"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        onClick={() => {
                          data.remove(listItemName)
                        }}
                      >
                        <CloseIcon /> Delete
                      </Button>
                    </Grid>
                  </Grid>
                )}
              />
            )}
            <Centered>
              <Grid container justify="center" spacing={6}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    mt={6}
                    onClick={() =>
                      formData.append('expected_inventory', {
                        sku: undefined,
                        num_units: undefined,
                      })
                    }
                  >
                    <AddIcon />
                    Add {expectedInventory?.length && 'More'} SKUs
                  </Button>
                </Grid>
                <Grid item>
                  <input
                    type="file"
                    accept=".csv"
                    ref={fileInput}
                    style={{ display: 'none' }}
                    onChange={onFileSelected}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    mt={6}
                    onClick={() => {
                      if (fileInput.current) {
                        fileInput.current.click()
                      }
                    }}
                  >
                    <AddIcon />
                    Add SKUs by CSV
                  </Button>
                </Grid>
              </Grid>
            </Centered>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={saving} autoFocus>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
